import { useState } from 'react';

import { MtxButton, MtxCheckbox } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import TutorialDialog from '@/components/TutorialDialog';
import VideoTutorialRow from '@/components/VideoTutorialRow';
import VideoTutorialsModal from '@/components/VideoTutorialsModal';
import VIDEO_TUTORIALS_MODAL_MODE from '@/enums/videoTutorialsModalMode';

import styles from './styles.module.css';

import type { TutorielVideo } from '@/types/videoTutorials';

interface PartnerTutorialsInterstitialProps {
  isOpen: boolean;
  onClose: () => void;
  tutorials: TutorielVideo[];
  partnerName: string;
  link: string;
  onPlay?: (tutorial: TutorielVideo) => void;
  onPressPartnerAccess?: () => void;
  onHidePartnerTutorials: () => void;
  onHideCheckboxChange?: (checked: boolean) => void;
  onOpenPlayback?: (tutorial: TutorielVideo) => void;
}

const PartnerTutorialsInterstitial = ({
  isOpen,
  onClose,
  tutorials,
  partnerName,
  link,
  onPlay,
  onPressPartnerAccess,
  onHidePartnerTutorials,
  onHideCheckboxChange,
  onOpenPlayback,
}: PartnerTutorialsInterstitialProps) => {
  const { t } = useTranslation();
  const [hideTutorials, setHideTutorials] = useState(false);
  const [modalTutorial, setModalTutorial] = useState<TutorielVideo>();
  const [wasOpen, setWasOpen] = useState(isOpen);

  // Réinitialisation à la fermeture pilotée par le parent : sans elle, la réouverture
  // relancerait la modale de lecture de l'ancien tuto, case encore cochée.
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setHideTutorials(false);
      setModalTutorial(undefined);
    }
  }

  const handlePartnerAccessPress = () => {
    if (hideTutorials) onHidePartnerTutorials();
    onPressPartnerAccess?.();
  };

  const handleHideCheckboxSelect = () => {
    const checked = !hideTutorials;
    setHideTutorials(checked);
    onHideCheckboxChange?.(checked);
  };

  const handleRowClick = (tutorial: TutorielVideo) => {
    setModalTutorial(tutorial);
    onOpenPlayback?.(tutorial);
  };

  const handleCtaClick = () => {
    handlePartnerAccessPress();
    globalThis.open(link, '_blank', 'noopener,noreferrer');
  };

  // La modale de lecture est une sœur de l'interstitiel, ni enfant ni portail : sinon ses événements
  // cancel / close / clic backdrop remonteraient jusqu'au <dialog> de l'interstitiel.
  return (
    <>
      <TutorialDialog
        isOpen={isOpen}
        title={t('videoTutorials.interstitialTitle', { partnerName })}
        subtitle={t('videoTutorials.interstitialText')}
        onClose={onClose}
        className={styles.partnerTutorialsInterstitial}
      >
        <ul className={styles.list}>
          {tutorials.map((tutorial) => (
            <li key={tutorial.videoId}>
              <VideoTutorialRow
                title={t(`videoTutorials.tutoTitle.${tutorial.operation}`, { partnerName })}
                duree={tutorial.duree}
                onClick={() => handleRowClick(tutorial)}
              />
            </li>
          ))}
        </ul>
        <div className={styles.checkbox}>
          <MtxCheckbox
            checkId="hide-partner-tutorials"
            checked={hideTutorials}
            onCheckboxSelect={handleHideCheckboxSelect}
          >
            {t('videoTutorials.hideTutorials')}
          </MtxCheckbox>
        </div>
        <MtxButton variant="primary" size="medium" fullWidth className={styles.cta} onClick={handleCtaClick}>
          {t('videoTutorials.partnerAccessCta')}
        </MtxButton>
      </TutorialDialog>
      <VideoTutorialsModal
        isOpen={isOpen && modalTutorial !== undefined}
        onClose={() => setModalTutorial(undefined)}
        entry={{ mode: VIDEO_TUTORIALS_MODAL_MODE.SINGLE, tutorial: modalTutorial ?? tutorials[0] }}
        partnerName={partnerName}
        onPlay={onPlay}
        onPartnerAccess={handleCtaClick}
      />
    </>
  );
};

export default PartnerTutorialsInterstitial;
